import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import waterpipe from "waterpipe";
import $ from "jquery";
import { useLanguage } from "brew-js-react";
import { combineRef, FormFieldProps, partial, useFormField, useMemoizedFunction, useRefInitCallback } from "zeta-dom-react";
import dom, { retainFocus } from "zeta-dom/dom";
import { getRect, matchSelector, removeNode, scrollIntoView, setClass } from "zeta-dom/domUtil";
import { exclude } from "zeta-dom/util";
import { runCSSTransition } from "zeta-dom/cssUtil";
import { Icon } from "src/components/common";
import { translate as t } from "src/i18n";
import { TextField, ValidatorProps, validators } from ".";

const flyout: HTMLElement = $('<div class="eac-date-picker">')[0];

export interface DatePickerProps extends FormFieldProps, ValidatorProps {
    label?: string;
    helpText?: string;
    placeholder?: string;
    required?: boolean;
}

function range<V>(from: number, to: number, callback: (v: number, i: number) => V) {
    return (waterpipe.eval('$0 .. $1', [from, to]) as number[]).map(callback);
}

function toNormalizedValue(date: Date) {
    return isNaN(+date) ? '' : waterpipe.eval(':date yyyy-MM-dd', date);
}

function toDisplayValue(value: string) {
    return value && t('common.dateFormat', value);
}

function DatePickerCalendar(props: { date: string, onSelectedDate: (value: string) => void }) {
    interface DatePickerState {
        selected?: Date;
        mode: 'd' | 'm' | 'y';
        month: Date;
        year: number;
    }

    interface DateItem {
        day: number;
        monthDelta: 1 | 0 | -1;
        className: string;
    }

    const language = useLanguage();
    const [state, setState] = useState<DatePickerState>({ mode: 'd', year: 0, month: new Date() });
    const curY = state.month.getFullYear();
    const curM = state.month.getMonth();

    const getPartialUpdate = useMemoizedFunction((delta: number) => {
        switch (state.mode) {
            case 'd':
                return { month: new Date(curY, curM + delta) };
            case 'y':
                return { year: state.year - (delta * 16) };
        }
        return {};
    });

    const days = useMemo(() => {
        const today = new Date();
        const d1 = curY === state.selected?.getFullYear() && curM === state.selected?.getMonth() && state.selected.getDate();
        const d2 = curY === today.getFullYear() && curM === today.getMonth() && today.getDate();

        const daysInMonth = new Date(curY, curM + 1, 0).getDate();
        const firstDayInWeek = new Date(curY, curM, 1).getDay();
        const daysInLastWeek = (firstDayInWeek + daysInMonth) % 7;

        const arr: DateItem[] = [];
        if (firstDayInWeek) {
            const daysInLastMonth = new Date(curY, curM, 0).getDate();
            arr.push(...range(1 - firstDayInWeek, 0, v => {
                return {
                    day: daysInLastMonth + v,
                    monthDelta: -1,
                    className: 'prev'
                } as DateItem;
            }))
        }
        arr.push(...range(1, daysInMonth, v => {
            return {
                day: v,
                monthDelta: 0,
                className: 'cur' + (d1 === v ? ' selected' : '') + (d2 === v ? ' today' : '')
            } as DateItem;
        }));
        if (daysInLastWeek) {
            arr.push(...range(1, 7 - daysInLastWeek, v => {
                return {
                    day: v,
                    monthDelta: 1,
                    className: 'next'
                } as DateItem;
            }));
        }
        return arr;
    }, [curY, curM, state.selected]);

    useEffect(() => {
        let selected: Date | undefined = new Date(props.date);
        let date = selected;
        if (isNaN(+selected)) {
            selected = undefined;
            date = new Date();
        }
        setState({
            selected,
            mode: 'd',
            month: new Date(date.getFullYear(), date.getMonth(), 1),
            year: date.getFullYear()
        });
    }, [props]);

    const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    return (
        <div ref={useRefInitCallback(initEvent)} className="eac-date-picker-wrapper">
            <div className="eac-date-picker-header">
                {state.mode !== 'm' &&
                    <Icon icon="arrowLeft" onClick={() => partial(setState)(getPartialUpdate(-1))} />}
                <span onClick={() => partial(setState)('mode', 'm')}>{new Intl.DateTimeFormat(language, { month: 'long' }).format(state.month)}</span>
                <span onClick={() => partial(setState)('mode', 'y')}>{new Intl.DateTimeFormat(language, { year: 'numeric' }).format(state.month)}</span>
                {state.mode !== 'm' &&
                    <Icon icon="arrowRight" onClick={() => partial(setState)(getPartialUpdate(1))} />}
            </div>
            {(state.mode === 'd' &&
                <div className="eac-date-picker-body body-d">
                    {weekdays.map((v, i) => (
                        <span key={i} className="weekday">{v}</span>
                    ))}
                    {days.map((v, i) => (
                        <span key={i} className={v.className} onClick={() => selectDate(v.day, v.monthDelta)}>{v.day}</span>
                    ))}
                </div>
            ) ||
                (state.mode === 'm' &&
                    <div className="eac-date-picker-body body-m">
                        {range(0, 11, (v, i) => {
                            return (
                                <span key={i} className={v === curM ? 'selected' : ''} onClick={() => partial(setState)({ mode: 'd', month: new Date(curY, v) })}>
                                    {new Intl.DateTimeFormat(language, { month: 'short' }).format(new Date(0, v))}
                                </span>
                            );
                        })}
                    </div>
                ) || (
                    <div className="eac-date-picker-body body-y">
                        {range(0, -15, (v, i) => {
                            v = v + state.year;
                            return (
                                <span key={i} className={v === curY ? 'selected' : ''} onClick={() => partial(setState)({ mode: 'd', month: new Date(v, curM) })}>{v}</span>
                            );
                        })}
                    </div>)}
        </div>
    );

    function selectDate(day: number, monthDelta: 1 | 0 | -1) {
        let date = new Date(curY, curM + monthDelta, day);
        partial(setState)({
            selected: date,
            ...getPartialUpdate(monthDelta)
        });
        props.onSelectedDate(toNormalizedValue(date));
    }

    function initEvent(element: HTMLElement) {
        dom.on(element, 'mousewheel', e => {
            partial(setState)(getPartialUpdate(e.data));
        });
    }
}

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>((props, ref) => {
    props = {
        ...props,
        onValidate: validators.combine(
            props.required && validators.required(),
            props.onValidate
        )
    };
    const language = useLanguage();
    const { value, error, setValue, elementRef } = useFormField(props, '');
    const [displayValue, setDisplayValue] = useState('');
    const focused = useRef(false);

    const updateDisplayValue = useMemoizedFunction(() => {
        setDisplayValue(toDisplayValue(value));
    });
    const updatePicker = useMemoizedFunction(() => {
        ReactDOM.render(<DatePickerCalendar date={value} onSelectedDate={setValue} />, flyout);
    });

    useEffect(() => {
        updateDisplayValue();
    }, [language, updateDisplayValue]);

    useEffect(() => {
        if (focused.current) {
            updatePicker();
            if (matchSelector(dom.activeElement, 'input')) {
                // do not update input value when it is focused
                return;
            }
        }
        updateDisplayValue();
    }, [value, updateDisplayValue, updatePicker]);

    function onChange(displayValue1: string) {
        if (!displayValue1) {
            setValue('');
        } else if (/\d{4}/.test(displayValue1) && /[-/]/.test(displayValue1)) {
            let d = new Date(displayValue1);
            if (!isNaN(+d)) {
                setValue(toNormalizedValue(d));
            }
        }
        setDisplayValue(displayValue1);
    }

    return (
        <TextField ref={combineRef(ref, elementRef, useRefInitCallback(initEvent))} {...exclude(props, ['name'])}
            toggle="calendar" error={error} value={displayValue} onChange={onChange} />
    );

    function initEvent(element: HTMLElement) {
        const input = element.querySelector('input')!;
        dom.on(element, 'focusin', e => {
            input.readOnly = e.source !== 'mouse';
            focused.current = true;
            element.firstElementChild!.appendChild(flyout);
            updatePicker();
            retainFocus(input, flyout);
            setClass(flyout, 'show-above', false);
            setClass(flyout, 'show-above', getRect(flyout).bottom > getRect(document.querySelector('[scrollable-target]')!).bottom);
            scrollIntoView(flyout);
            setClass(flyout, 'open', true);
            setClass(flyout, 'closing', false);
        });
        dom.on(element, 'focusout', () => {
            focused.current = false;
            runCSSTransition(flyout, 'closing', true).then(() => {
                setClass(flyout, 'open', false);
                if (element.contains(flyout.parentElement)) {
                    removeNode(flyout);
                }
            });
            updateDisplayValue();
        });
    }
});
